import { CopilotCitation } from "./copilotStudioClient"
import { isTableSeparatorRow } from "./copilotTables"
import { BRAND_ACCESS_DENIED, citationIsAllowed, CopilotBrandScope } from "./copilotBrandScope"
import {
  fileNameFromUrl,
  markerNumbers,
  withoutTruncatedLinks,
  readableSourceLinks,
  sameDocumentUrl,
  UNSUPPORTED_MARK,
} from "./copilotCitations"

const STOP_WORDS = new Set([
  "what",
  "which",
  "when",
  "where",
  "with",
  "from",
  "this",
  "that",
  "have",
  "does",
  "about",
  "please",
  "sharepoint",
  "document",
  "documents",
  "reader",
])

export const SHAREPOINT_RETRIEVAL_SUCCESS =
  "A source counts only when the answer and the source list use the same https SharePoint link, that link opens the named document, it is inside your allowed brands, and the file name matches this question. Each factual claim must name that document in the same sentence or table row. The answer must explain what the document says and how it applies, not only list the link."

export type ReviewedSource = CopilotCitation & {
  status: "verified" | "unverified"
  reason: string
}

export type SharePointReview = {
  sources: ReviewedSource[]
  grounded: boolean
  summary: string
  /** Long grounding rules for an info tip — not shown as a wall of text. */
  rulesTip: string
  unsupportedClaims: string[]
  /** LAB-113: the answer with each unsupported sentence or table row marked. */
  annotated: string
}

function decoded(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function tokens(value: string) {
  return decoded(value)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 4 && !STOP_WORDS.has(token))
}

export type MarkdownLink = { title: string; url: string; start: number; end: number }

export function findMarkdownLinks(text: string): MarkdownLink[] {
  const links: MarkdownLink[] = []
  const opener = /\[([^\]]+)\]\(/g
  let match = opener.exec(text)
  while (match) {
    const urlStart = match.index + match[0].length
    let depth = 1
    let cursor = urlStart
    while (cursor < text.length && depth > 0 && !/\s/.test(text[cursor])) {
      if (text[cursor] === "(") depth += 1
      else if (text[cursor] === ")") depth -= 1
      if (depth > 0) cursor += 1
    }
    const url = text.slice(urlStart, cursor).trim()
    const end = depth === 0 ? cursor + 1 : cursor
    if (/^https?:\/\//i.test(url)) {
      links.push({ title: match[1], url, start: match.index, end })
    }
    opener.lastIndex = Math.max(end, match.index + 1)
    match = opener.exec(text)
  }
  return links
}

export function citationsFromText(raw: string): CopilotCitation[] {
  // LAB-113: a pasted SharePoint path with spaces is one link, not ".../teams/tec/Scientific".
  const text = readableSourceLinks(raw)
  const citations: CopilotCitation[] = []
  const seen = new Set<string>()
  const add = (title: string, url: string) => {
    const cleanUrl = url.trim().replace(/[),.;]+$/g, "")
    if (!cleanUrl || seen.has(cleanUrl)) return
    seen.add(cleanUrl)
    const fileName = fileNameFromUrl(cleanUrl)
    citations.push({
      title: title.trim() || fileName || cleanUrl,
      url: cleanUrl,
    })
  }
  findMarkdownLinks(text).forEach((link) => add(link.title, link.url))
  const footnotes = /\[\d+\]:\s+(https?:\/\/\S+)/g
  let match = footnotes.exec(text)
  while (match) {
    add("", match[1])
    match = footnotes.exec(text)
  }
  // Plain SharePoint / https links the agent pastes without markdown.
  const bare = /https:\/\/[^\s<>\])"']+/gi
  let bareMatch = bare.exec(text)
  while (bareMatch) {
    add("", bareMatch[0])
    bareMatch = bare.exec(text)
  }
  return citations
}

function decodedPath(url: string) {
  try {
    return decodeURIComponent(new URL(url).pathname)
  } catch {
    return decoded(url)
  }
}

function documentStem(url: string) {
  const name = (decodedPath(url).split("/").pop() || "")
    .replace(/[\u2000-\u200B\u202F\u205F\u00A0]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
  return name
    .replace(/\.[a-z0-9]{2,5}$/i, "")
    .replace(/\)+$/g, "")
    .replace(/\s*\(\d+$/g, "")
    .trim()
}

function sameDocument(left: string, right: string) {
  const a = documentStem(left)
  const b = documentStem(right)
  return a.length >= 8 && a === b
}

export function citationUrlIsDamaged(url: string) {
  const path = decodedPath(url)
  if (/[\u2000-\u200B\u202F\u205F]/.test(path)) return true
  const name = (path.split("/").pop() || "").trim()
  const open = name.lastIndexOf("(")
  return open >= 0 && name.lastIndexOf(")") < open
}

function citationScore(citation: CopilotCitation) {
  const url = citation.url || ""
  let score = 0
  if (sharePointDocumentUrl(url)) score += 5
  if (url && !citationUrlIsDamaged(url)) score += 5
  if (/\.(pdf|docx?|pptx?|xlsx?)($|\?)/i.test(url)) score += 2
  if (/\.(pdf|docx?|pptx?|xlsx?)$/i.test(citation.title)) score += 1
  return score + Math.min(url.length, 500) / 1000
}

function replaceWholeUrl(text: string, from: string, to: string) {
  if (!from || from === to) return text
  const escaped = from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  return text.replace(new RegExp(`${escaped}(?![A-Za-z0-9._~:/?#\\[\\]@!$&'*+,;=%.)-])`, "g"), to)
}

export function alignSharePointCitations(text: string, citations: CopilotCitation[]) {
  const found = mergeCitations(citations, citationsFromText(text))
  const groups: CopilotCitation[][] = []
  found.forEach((item) => {
    const group = item.url
      ? groups.find((existing) => existing.some((other) => other.url && sameDocument(other.url, item.url || "")))
      : undefined
    if (group) group.push(item)
    else groups.push([item])
  })
  let repaired = text
  const aligned: CopilotCitation[] = []
  groups.forEach((group) => {
    const ranked = [...group].sort((left, right) => citationScore(right) - citationScore(left))
    const best = ranked[0]
    const canonical = best.url
    aligned.push(best)
    if (!canonical) return
    ranked.slice(1).forEach((item) => {
      const from = item.url
      if (!from || from === canonical) return
      const links = findMarkdownLinks(repaired).filter((link) => link.url === from).reverse()
      links.forEach((link) => {
        const label = /\.(pdf|docx?|pptx?|xlsx?)$/i.test(best.title) ? best.title : link.title
        repaired = `${repaired.slice(0, link.start)}[${label}](${canonical})${repaired.slice(link.end)}`
      })
      repaired = replaceWholeUrl(repaired, from, canonical)
    })
  })
  return { text: repaired, citations: aligned }
}

export function mergeCitations(
  primary: CopilotCitation[],
  extra: CopilotCitation[]
): CopilotCitation[] {
  const seen = new Set<string>()
  const merged: CopilotCitation[] = []
  ;[...primary, ...extra].forEach((citation) => {
    const key = (citation.url || citation.title).trim().toLowerCase()
    if (!key || seen.has(key)) return
    seen.add(key)
    merged.push(citation)
  })
  return merged
}

export function citesSharePointDocument(text: string, citations: CopilotCitation[]) {
  return [...citations, ...citationsFromText(text)].some(
    (citation) => citation.url && sharePointDocumentUrl(citation.url)
  )
}

function sharePointDocumentUrl(url: string) {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== "https:") return false
    if (!/(^|\.)sharepoint\.com$/i.test(parsed.hostname)) return false
    const path = decoded(`${parsed.pathname}${parsed.search}`).toLowerCase()
    if (path.length < 8) return false
    return /\/sites\/|\/:[a-z]:\/|shared documents|doc\.aspx|\.(pdf|docx?|pptx?|xlsx?|csv)(\?|$)/i.test(
      path
    )
  } catch {
    return false
  }
}

function sourceIsRelevant(question: string, answer: string, citation: CopilotCitation) {
  const questionTokens = tokens(question)
  const haystack = decoded(`${citation.title} ${citation.url || ""}`).toLowerCase()
  if (questionTokens.some((token) => haystack.includes(token))) return true
  return tokens(citation.title).some((token) => answer.toLowerCase().includes(token))
}

function isKeyClaim(block: string) {
  if (/^\[\d+\]:\s*https?:/i.test(block)) return false
  if (/\?\s*$/.test(block)) return false
  const prose = block.replace(/\|/g, " ").replace(/\s+/g, " ").trim()
  if (prose.length < 40) return false
  return (
    /\d/.test(prose) ||
    /\b(recommend|measures|measure|supports|includes|provides|because|suitable|offer|according|range|relevant|ideal|best[-\s]fit)\b/i.test(prose)
  )
}

function citationFileLabel(citation: CopilotCitation) {
  const fromUrl = citation.url ? fileNameFromUrl(citation.url) : ""
  const title = (citation.title || "").trim()
  if (/\.(pdf|docx?|pptx?|xlsx?)($|\?)/i.test(title)) return title
  return fromUrl || title
}

/** The block links or names the document itself. */
function namesCitedDocument(block: string, citation: CopilotCitation) {
  const linked = findMarkdownLinks(block).some(
    (link) => link.url && citation.url && (link.url === citation.url || sameDocumentUrl(link.url, citation.url))
  )
  if (linked) return true
  const lower = block.toLowerCase()
  if (citation.url && lower.includes(citation.url.toLowerCase())) return true
  const fileLabel = citationFileLabel(citation).toLowerCase()
  if (fileLabel && lower.includes(fileLabel)) return true
  const stem = documentStem(citation.url || citation.title || "")
  return stem.length >= 6 && lower.includes(stem)
}

/** The block refers to "the brochure" or shares distinctive words with the document title. */
function refersToCitedDocument(block: string, citation: CopilotCitation) {
  const lower = block.toLowerCase()
  if (/\bsharepoint\b/.test(lower) && /\b(brochure|manual|document|documents|guide|specification|spec)\b/.test(lower)) {
    return true
  }
  if (/\b(the|this)\s+(brochure|manual|document|guide)\b/.test(lower)) return true
  const words = tokens(citation.title || "").filter((word) => word.length >= 5)
  if (!words.length) return false
  const hits = words.filter((word) => lower.includes(word))
  return hits.some((word) => word.length >= 6) || hits.length >= 2
}

/**
 * LAB-113: does a [n] marker in the text point at a cited document? true / false when
 * it has markers, null when it has none. Without a marker map (an older chat whose
 * markers cannot be matched), a marker counts when the answer has verified sources.
 */
function markersSupport(text: string, cited: CopilotCitation[], markers?: CopilotCitation[]) {
  const numbers = markerNumbers(text)
  if (!numbers.length) return null
  if (!markers?.length) return cited.length > 0
  return numbers.some((number) => {
    const source = markers.find((item) => item.position === number)
    return Boolean(source?.url && cited.some((citation) => sameDocumentUrl(citation.url, source.url)))
  })
}

function blockSupported(block: string, cited: CopilotCitation[], markers?: CopilotCitation[]) {
  const byMarker = markersSupport(block, cited, markers)
  if (byMarker) return true
  if (cited.some((citation) => namesCitedDocument(block, citation))) return true
  // A marker that points at a website must not be outvoted by a loose word match.
  return byMarker === null && cited.some((citation) => refersToCitedDocument(block, citation))
}

/** "DCR vs. MCR 53" is one sentence. Splitting after "vs." left "MCR 53 ..." looking like a bare spec claim. */
const ENDS_WITH_ABBREVIATION = /\b(?:vs|approx|e\.g|i\.e|incl|fig)\.$/i

function claimSentences(block: string) {
  if (block.includes("|")) return [block]
  const sentences: string[] = []
  block
    // "...substrate.[1] The next" ends a sentence too.
    .split(/(?<=[.!?](?:\[\d+\])*)\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean)
    .forEach((sentence) => {
      const last = sentences.length - 1
      if (last >= 0 && ENDS_WITH_ABBREVIATION.test(sentences[last])) {
        sentences[last] = `${sentences[last]} ${sentence}`
      } else {
        sentences.push(sentence)
      }
    })
  return sentences
}

/**
 * "The documents do not state X" has no document to cite, and the grounding rules
 * (LAB-105) ask the agent to say exactly that when a detail is missing. Flagging it
 * as an unsupported claim showed a grounding warning on the most careful answers.
 */
const STATES_ABSENCE = new RegExp(
  [
    "\\bnot\\s+(?:stated|specified|described|documented|detailed|mentioned|covered|addressed|included|contained|present|available|retrievable|found|given)\\b",
    "\\b(?:do|does|did)\\s+not\\s+(?:state|specify|describe|document|detail|mention|cover|address|include|contain|give|list|provide)\\b",
    "\\b(?:is|are)\\s+not\\s+in\\s+(?:the|these|those|any)\\s+(?:\\w+\\s+)?(?:documents?|sources?|files?)\\b",
    "\\b(?:no|none\\s+of\\s+the)\\s+(?:\\w+\\s+){0,3}(?:documents?|sources?|files?|release\\s+notes?)\\b",
    "\\bI\\s+(?:cannot|can't|won't|will\\s+not)\\s+(?:confirm|cite|give|state|guess|list)\\b",
  ].join("|"),
  "i"
)

function statesAbsence(sentence: string) {
  return STATES_ABSENCE.test(sentence)
}

/**
 * Sentences about the answer itself rather than the product (LAB-105 retest): a closing
 * offer ("If you'd like, I can draft a DCR vs. MCR 53 table") or a note on search hits
 * the agent set aside ("hits for DCR relate to Snowflake Data Clean Rooms, which are not
 * relevant"). Neither has a document to cite, so neither is an unsupported claim.
 */
const OFFERS_MORE =
  /^(?:if\s+(?:you(?:['’]d|\s+would)?\s+(?:like|want|prefer|need)|helpful|useful)|would\s+you\s+like|do\s+you\s+want|shall\s+I|want\s+me\s+to|let\s+me\s+know|next\s+steps?:|I\s+can\s+(?:also\s+)?(?:prepare|produce|draft|create|build|make|add|share|provide|put\s+together|expand|go\s+deeper))/i
const SEARCH_NOTE =
  /\b(?:search(?:es)?|hits?|results?|matches)\b.*\b(?:unrelated|irrelevant|not\s+relevant|off[-\s]topic|ignored|excluded|set\s+aside)\b/i

/** "You asked for a DCR vs. MCR 53 comparison" restates the question. */
const RESTATES_QUESTION = /^(?:you(?:['’]ve)?\s+(?:asked|wanted|requested|are\s+asking)|here\s+(?:is|are)\s+(?:a|an|the)\b)/i

function isAside(sentence: string) {
  const text = sentence
    .replace(/^[\s>*_•(-]+/, "")
    .replace(/^\d+[.)]\s*/, "")
    .replace(/^note:[\s*]*/i, "")
  return OFFERS_MORE.test(text) || SEARCH_NOTE.test(text) || RESTATES_QUESTION.test(text)
}

function isInference(sentence: string) {
  return /\b(because|this matters|so that|which means|as a result|therefore)\b/i.test(sentence)
}

type ClaimCheck = { line: number; sentence: string; supported: boolean }

function isClaimLine(line: string) {
  return Boolean(line) && !/^[-*_]{3,}$/.test(line) && !/^\|?\s*:?-{3,}/.test(line) && !/^#{1,3}\s/.test(line)
}

/** A table's first row names its columns; it is not a claim. */
function isTableHeader(lines: string[], index: number) {
  const next = lines.slice(index + 1).find((line) => line.trim())
  return lines[index].includes("|") && Boolean(next && isTableSeparatorRow(next))
}

/** Words that frame a recommendation; they carry no fact a document has to hold. */
const FRAMING_WORDS = new Set([
  "offer",
  "recommend",
  "recommended",
  "choose",
  "because",
  "since",
  "therefore",
  "ideal",
  "best",
  "suitable",
  "customer",
  "customers",
])

function claimTerms(text: string) {
  const clean = text.replace(/\[\d+\]/g, " ").replace(/[*_`|]/g, " ")
  return {
    numbers: new Set(clean.match(/\d+(?:[.,]\d+)?/g) || []),
    words: new Set(tokens(clean).filter((word) => !/^\d/.test(word) && !FRAMING_WORDS.has(word))),
  }
}

/**
 * A closing recommendation often repeats a claim made, and cited, earlier in the answer
 * (LAB-113). It is supported when one cited sentence or passage holds all of its numbers
 * and most of its words.
 */
function restatesSupported(sentence: string, supported: string[]) {
  const own = claimTerms(sentence)
  const total = own.numbers.size + own.words.size
  if (total < 3) return false
  return supported.some((text) => {
    const other = claimTerms(text)
    if ([...own.numbers].some((number) => !other.numbers.has(number))) return false
    const hits = [...own.words].filter((word) => other.words.has(word)).length + own.numbers.size
    return hits / total >= 0.6
  })
}

function checkClaims(answer: string, citations: CopilotCitation[], markers?: CopilotCitation[]) {
  const cited = citations.filter((citation) => citation.url || citation.title)
  const lines = answer.split("\n")
  const checks: ClaimCheck[] = []
  // When the agent cites with markers, a marker covers the sentences before it back to the
  // previous marker; a sentence after a paragraph's last marker was left uncited.
  const citesWithMarkers = Boolean(markers?.length)
  lines.forEach((raw, index) => {
    const block = raw.trim()
    if (!isClaimLine(block) || isTableHeader(lines, index)) return
    const blockCited = blockSupported(block, cited, markers)
    const sentences = claimSentences(block)
    sentences.forEach((sentence, position) => {
      const closingMarker = sentences.slice(position + 1).find((later) => markerNumbers(later).length)
      if (
        blockSupported(sentence, cited, markers) ||
        (closingMarker && !markerNumbers(sentence).length && markersSupport(closingMarker, cited, markers))
      ) {
        checks.push({ line: index, sentence, supported: true })
        return
      }
      if (statesAbsence(sentence) || isAside(sentence)) return
      const inference = isInference(sentence) && sentence.replace(/\|/g, " ").trim().length >= 40
      if (blockCited && !inference && !citesWithMarkers) {
        checks.push({ line: index, sentence, supported: true })
        return
      }
      if (!inference && !isKeyClaim(sentence)) return
      checks.push({ line: index, sentence, supported: false })
    })
  })
  const supportedTexts = [
    ...checks.filter((check) => check.supported).map((check) => check.sentence),
    ...(markers || [])
      .filter((marker) => marker.excerpt && cited.some((citation) => sameDocumentUrl(citation.url, marker.url)))
      .map((marker) => marker.excerpt as string),
  ]
  return checks.map((check) =>
    check.supported || !restatesSupported(check.sentence, supportedTexts)
      ? check
      : { ...check, supported: true }
  )
}

function annotate(answer: string, unsupported: ClaimCheck[]) {
  const lines = answer.split("\n")
  const byLine = new Map<number, string[]>()
  unsupported.forEach((check) => {
    byLine.set(check.line, [...(byLine.get(check.line) || []), check.sentence])
  })
  byLine.forEach((sentences, index) => {
    let line = lines[index]
    if (/\|\s*$/.test(line)) {
      lines[index] = line.replace(/\s*\|\s*$/, ` ${UNSUPPORTED_MARK} |`)
      return
    }
    let cursor = 0
    sentences.forEach((sentence) => {
      const at = line.indexOf(sentence, cursor)
      if (at < 0) {
        line = `${line} ${UNSUPPORTED_MARK}`
        cursor = line.length
        return
      }
      const end = at + sentence.length
      line = `${line.slice(0, end)} ${UNSUPPORTED_MARK}${line.slice(end)}`
      cursor = end + UNSUPPORTED_MARK.length + 1
    })
    lines[index] = line
  })
  return lines.join("\n")
}

function claimText(sentence: string) {
  return sentence.replace(/\|/g, " ").replace(/\s+/g, " ").trim().slice(0, 160)
}

/**
 * Key claims with no supporting document. `markers` maps each [n] to its source; a
 * claim whose marker points at a website is not tied to a cited document.
 */
export function unsupportedClaims(answer: string, citations: CopilotCitation[], markers?: CopilotCitation[]) {
  return checkClaims(answer, citations, markers)
    .filter((check) => !check.supported)
    .map((check) => claimText(check.sentence))
}

function answerHasDepth(answer: string) {
  const prose = answer
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\[[^\]]+\]/g, "")
    .trim()
  const sentences = prose.split(/[.!?]/).filter((sentence) => sentence.trim().length > 40)
  return prose.length >= 180 || sentences.length >= 2
}

export function isCopilotCapacityMessage(answer: string) {
  return /\breached its usage limit\b|\bagent is currently unavailable\b|\bbilling issue\b/i.test(
    answer.trim()
  )
}

export function reviewSharePointSources(input: {
  question: string
  answer: string
  citations: CopilotCitation[]
  scope: CopilotBrandScope | null
  /** LAB-113: the source of each [n] marker, from positionedCitations. */
  markers?: CopilotCitation[]
}): SharePointReview | null {
  const question = input.question.trim()
  if (!question || input.answer === BRAND_ACCESS_DENIED) return null
  if (isCopilotCapacityMessage(input.answer)) return null
  const aligned = alignSharePointCitations(input.answer, withoutTruncatedLinks(input.citations))
  const { citations } = aligned
  const sources: ReviewedSource[] = citations.map((citation) => {
    if (!citation.url) {
      return { ...citation, status: "unverified", reason: "No document link" }
    }
    if (citationUrlIsDamaged(citation.url)) {
      return { ...citation, status: "unverified", reason: "This link does not open the document" }
    }
    if (!sharePointDocumentUrl(citation.url)) {
      return {
        ...citation,
        status: "unverified",
        reason: "Not an https SharePoint document link",
      }
    }
    if (!citationIsAllowed(citation, input.scope)) {
      return { ...citation, status: "unverified", reason: "Outside your assigned brands" }
    }
    if (!sourceIsRelevant(question, input.answer, citation)) {
      return {
        ...citation,
        status: "unverified",
        reason: "File name does not match this question",
      }
    }
    return { ...citation, status: "verified", reason: "Verified SharePoint source" }
  })
  const sharePointSources = sources.filter((source) => source.url && /sharepoint\.com/i.test(source.url))
  const reviewed = sharePointSources.length ? sharePointSources : sources
  const verified = reviewed.filter((source) => source.status === "verified")
  const deep = answerHasDepth(aligned.text)
  const checks = checkClaims(aligned.text, verified, input.markers)
  const unsupported = checks.filter((check) => !check.supported)
  const claims = unsupported.map((check) => claimText(check.sentence))
  const annotated = annotate(aligned.text, unsupported)
  const rulesTip = SHAREPOINT_RETRIEVAL_SUCCESS
  let summary = "Sources verified against SharePoint."
  let grounded = verified.length > 0 && verified.length === reviewed.length && deep && claims.length === 0
  if (!verified.length) {
    summary = "This answer is not grounded in a verified SharePoint document."
    grounded = false
  } else if (verified.length !== reviewed.length) {
    summary = "Some links could not be verified as SharePoint documents."
    grounded = false
  } else if (claims.length) {
    summary =
      claims.length === 1
        ? "1 statement is not tied to a cited document. It is labelled in the answer."
        : `${claims.length} statements are not tied to a cited document. Each is labelled in the answer.`
    grounded = false
  } else if (!deep) {
    summary = "Sources match, but the answer does not explain what those documents say."
    grounded = false
  }
  return { sources: reviewed, grounded, summary, rulesTip, unsupportedClaims: claims, annotated }
}

/** LabWizard product photos already name their website source. SharePoint checks do not apply to that gallery. */
export function reviewBesideProductPhotos(
  review: SharePointReview | null,
  imageBlock: string
): SharePointReview | null {
  if (!review) return null
  if (/Product images from LabWizard sources/i.test(imageBlock || "")) return null
  if (!/!\[[^\]]*\]\(https?:\/\//i.test(imageBlock || "")) return review
  const sources = review.sources.filter((source) => source.status === "verified")
  if (!sources.length && !review.unsupportedClaims.length) return null
  if (sources.length === review.sources.length) return review
  const grounded = sources.length > 0 && review.unsupportedClaims.length === 0
  return {
    ...review,
    sources,
    grounded,
    summary: grounded ? "Sources verified against SharePoint." : review.summary,
  }
}
